import React, { useState, useEffect } from 'react'
import SystemSettingForm from '../components/SystemSettingForm'
import CompanyPartnersManager from '../components/CompanyPartnersManager'
import CompanyDataForm from '../components/CompanyDataForm'
import ContractSettingsPage from './ContractSettingsPage'
import InvoiceNotificationSettings from '../components/InvoiceNotificationSettings'
import { apiService } from '../services/apiService'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  Cog6ToothIcon,
  AdjustmentsHorizontalIcon,
  UsersIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  BellIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const SystemSettingsPage = () => {
  const [activeTab, setActiveTab] = useState('company') // 'company', 'settings', 'partners', 'contract-settings' or 'invoice-notifications'
  const [settings, setSettings] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingSetting, setEditingSetting] = useState(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const result = await apiService.getSystemSettings()
      setSettings(result.data || [])
    } catch (error) {
      console.error('Erro ao carregar configurações:', error)
      setSettings([])
      toast.error('Erro ao carregar configurações')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingSetting(null)
    setShowForm(true)
  }

  const handleEdit = (setting) => {
    setEditingSetting(setting)
    setShowForm(true)
  }

  const handleView = (setting) => {
    toast(`Configuração: ${setting.key_name} = ${setting.value}`, {
      icon: 'ℹ️',
    })
  }

  const handleDelete = async (id, keyName) => {
    if (!confirm(`Tem certeza que deseja remover a configuração "${keyName}"?`)) {
      return
    }

    try {
      await apiService.deleteSystemSetting(id)
      toast.success('Configuração removida com sucesso!')
      loadSettings()
    } catch (error) {
      console.error('Erro ao remover configuração:', error)
      toast.error('Erro ao remover configuração')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingSetting(null)
  }

  const handleFormSuccess = () => {
    loadSettings()
    setShowForm(false)
    setEditingSetting(null)
  }

  const filteredSettings = settings.filter(setting => {
    const searchLower = searchQuery.toLowerCase()
    return (
      setting.key_name.toLowerCase().includes(searchLower) ||
      setting.category.toLowerCase().includes(searchLower) ||
      (setting.description && setting.description.toLowerCase().includes(searchLower))
    )
  })

  const getCategoryColor = (category) => {
    const colors = {
      'general': 'bg-blue-100 text-blue-800',
      'financial': 'bg-green-100 text-green-800',
      'notifications': 'bg-yellow-100 text-yellow-800',
      'maintenance': 'bg-purple-100 text-purple-800'
    }
    return colors[category] || 'bg-gray-100 text-gray-800'
  }

  const getCategoryLabel = (category) => {
    const labels = {
      'general': 'Geral',
      'financial': 'Financeiro',
      'notifications': 'Notificações',
      'maintenance': 'Manutenção'
    }
    return labels[category] || category
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando configurações...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Cog6ToothIcon className="h-8 w-8 text-blue-600 mr-3" />
            Configurações do Sistema
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie as configurações gerais do sistema e sócios da empresa
          </p>
        </div>

        {activeTab === 'settings' && (
          <button
            onClick={handleAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Nova Configuração</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('company')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
              ${activeTab === 'company'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <BuildingOfficeIcon className="h-5 w-5" />
            <span>Dados da Empresa</span>
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
              ${activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
            <span>Configurações Gerais</span>
          </button>

          <button
            onClick={() => setActiveTab('partners')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
              ${activeTab === 'partners'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <UsersIcon className="h-5 w-5" />
            <span>Sócios e Parceiros</span>
          </button>

          <button
            onClick={() => setActiveTab('contract-settings')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
              ${activeTab === 'contract-settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <DocumentTextIcon className="h-5 w-5" />
            <span>Definições de Contrato</span>
          </button>

          <button
            onClick={() => setActiveTab('invoice-notifications')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
              ${activeTab === 'invoice-notifications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <BellIcon className="h-5 w-5" />
            <span>Notificações de Faturas</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'company' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <CompanyDataForm onSuccess={loadSettings} />
        </div>
      )}

      {activeTab === 'settings' && (
        <>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por chave, categoria ou descrição..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {filteredSettings.length === 0 ? (
          <div className="text-center py-12">
            <AdjustmentsHorizontalIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'Nenhuma configuração encontrada' : 'Backend implementado'}
            </h3>
            <p className="text-gray-500 mb-4">
              API funcionando. Frontend completo em desenvolvimento.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chave
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descrição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSettings.map((setting) => (
                  <tr key={setting.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 font-mono">
                        {setting.key_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(setting.category)}`}>
                        {getCategoryLabel(setting.category)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {setting.value || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {setting.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(setting)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Visualizar"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {setting.editable && (
                          <button
                            onClick={() => handleEdit(setting)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="Editar"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {setting.editable && (
                          <button
                            onClick={() => handleDelete(setting.id, setting.key_name)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Remover"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

          {/* Modal do Formulário */}
          {showForm && (
            <SystemSettingForm
              systemSetting={editingSetting}
              onClose={handleFormClose}
              onSuccess={handleFormSuccess}
            />
          )}
        </>
      )}

      {/* Partners Tab */}
      {activeTab === 'partners' && (
        <CompanyPartnersManager />
      )}

      {/* Contract Settings Tab */}
      {activeTab === 'contract-settings' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <ContractSettingsPage />
        </div>
      )}

      {/* Invoice Notifications Tab */}
      {activeTab === 'invoice-notifications' && (
        <InvoiceNotificationSettings />
      )}
    </div>
  )
}

export default SystemSettingsPage